import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { useCallback, useContext, useEffect, useState } from "react";
import { MdHighlightOff } from "react-icons/md";
import { getAllGatewaysByClient } from "api/graphQL_gateways";
import { getAllOrganizationsSecond } from "api/graphQL_organizations";
import { ClientContext } from "ClientProvider";
import DivLoader from "components/divloader/DivLoader";
import { Select as AntSelect } from "antd";
import { getClientsSecond } from "api/graphQL_clients";
import { FiUploadCloud, FiXCircle } from "react-icons/fi";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import Batch from "../../../../../assets/csv/CryptoPayin.csv";
import Papa from "papaparse";
import { createBatchPayin } from "api/graphQL_crypto";

interface iCreateBatchForm {
  org_id: string; client_id: string; approval_type: string; batch_file: any; gateway: any;
}
export type AllGatewayObj = { name: string; id: string; };

/* ── Dropzone ── */
const FileDropzone = ({ label, onFileSelect, onRemoveFile }: {
  label: string; onFileSelect: (file: string | null) => void; onRemoveFile: any;
}) => {
  const [fileName, setFileName] = useState("");
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const allowed = ["text/csv","application/vnd.ms-excel"];
      if (allowed.includes(file.type)) {
        setFileName(file.name);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => onFileSelect(reader.result as string);
      } else { toast.error("Only CSV files are allowed."); setFileName(""); onFileSelect(""); }
    }
  }, [onFileSelect]);
  const removeFile = () => { onRemoveFile(true); setFileName(""); onFileSelect(""); };
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <div {...getRootProps()} className="cpi-dropzone">
      <input {...getInputProps()} />
      {fileName ? (
        <div className="cpi-dropzone-file">
          <FiUploadCloud size={16} style={{ color:"#16a34a" }} />
          <span className="cpi-dropzone-filename">{fileName}</span>
          <button className="cpi-dropzone-remove" onClick={(e) => { e.stopPropagation(); removeFile(); }}>
            <FiXCircle size={14} />
          </button>
        </div>
      ) : (
        <div className="cpi-dropzone-placeholder">
          <FiUploadCloud size={28} style={{ color:"#7c3aed", marginBottom:"8px" }} />
          <p className="cpi-dropzone-label">{label}</p>
        </div>
      )}
    </div>
  );
};

/* ── Main component ── */
const AddBatchFile = ({ fetchPayouts }: { fetchPayouts: () => void }) => {
  const rootForm: iCreateBatchForm = { org_id:"", client_id:"", approval_type:"", batch_file:"", gateway:"" };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [page] = useState(1); const [pageSize] = useState(500); const [searchTexts] = useState("");
  const [allOrg, setAllOrg] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [formValues, setFormValues] = useState<iCreateBatchForm>(rootForm);
  const [formValuesErr, setFormValuesErr] = useState<iCreateBatchForm>(rootForm);
  const [isLoading, setIsLoading] = useState(false);
  const [batchFile, setBatchFile] = useState<string | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const { singleRoleData } = useContext(ClientContext);
  const [allGateways, setAllGateways] = useState<AllGatewayObj[]>([]);

  const handleClose = () => {
    setFormValues(rootForm); setFormValuesErr(rootForm); setBatchFile("");
    setIsCloseModal(true); setClients([]); setAllGateways([]);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const fetchOrganizations = () => {
    getAllOrganizationsSecond(page, pageSize, searchTexts).then((data) => {
      const mapped = data?.[0]?.map((e: any) => ({ name: e.name, id: e.id }));
      setOrganizations(mapped); setAllOrg(mapped);
    });
  };

  const fetchGateways = (client_id: any) => {
    getAllGatewaysByClient(client_id).then((data) => {
      const filtered = (data ?? []).filter((gw: any) =>
        gw?.payment_method?.filterPaymentMethod?.some((m: any) => m.name === "crypto payin" && m.is_Active === true)
      );
      setAllGateways(filtered.map((item: any) => ({ ...item, id: item.gateway_id })));
    }).catch(console.error);
  };

  const fetchClients = (org_id: any) => {
    if (!org_id) { setClients([]); return; }
    const name = allOrg.find(o => o.id === org_id)?.name || '';
    getClientsSecond({ page, per_page: pageSize, searchTexts: name })
      .then((data) => setClients(data?.[0]?.map((e: any) => ({ name: e.name, id: e.client_id }))));
  };

  useEffect(() => {
    if (typeof batchFile === "string" && batchFile.trim() !== "") {
      setRemoveFile(false);
      setFormValues((p: any) => ({ ...p, batch_file: batchFile }));
      setFormValuesErr((p: any) => ({ ...p, batch_file: "" }));
    }
  }, [batchFile]);

  useEffect(() => {
    if (removeFile) {
      setFormValues((p: any) => ({ ...p, batch_file: "" }));
      setFormValuesErr((p: any) => ({ ...p, batch_file: "The field is required" }));
    }
  }, [removeFile]);

  const REQUIRED_HEADERS = ["invoice_currency","invoice_amount","payment_currency","payment_chain","customer_id","external_customer_no"];
  const validateCSV = (csvDataUri: string) => {
    const csvText = atob(csvDataUri.replace(/^data:text\/csv;base64,/, ''));
    const parsed = Papa.parse<Record<string, string>>(csvText, { header: true, skipEmptyLines: true });
    if (parsed.errors.length > 0) { toast.error("CSV parsing failed, please download sample CSV"); return false; }
    const actualHeaders = Object.keys(parsed.data[0] || {});
    const missing = REQUIRED_HEADERS.filter(h => !actualHeaders.includes(h));
    if (missing.length > 0) { toast.error(`Missing headers: ${missing.map(h => h.replace(/_/g,' ')).join(", ")}`); return false; }
    for (let i = 0; i < parsed.data.length; i++) {
      const row = parsed.data[i];
      for (const header of REQUIRED_HEADERS) {
        if (!row[header] || String(row[header]).trim() === "") {
          toast.error(`Row ${i + 2}: "${header.replace(/_/g,' ')}" is missing. Please download sample CSV.`);
          return false;
        }
      }
    }
    return true;
  };

  function validateData() {
    let v: any = {};
    if (!formValues?.org_id) v.org_id = "The field is required";
    if (!formValues?.client_id) v.client_id = "The field is required";
    if (!formValues?.gateway) v.gateway = "The field is required";
    if (!formValues?.batch_file) v.batch_file = "The field is required";
    if (Object.keys(v).length > 0) { setFormValuesErr(v); return false; }
    return true;
  }

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validateData()) return;
    if (!validateCSV(formValues.batch_file)) return;
    setIsLoading(true);
    createBatchPayin({ client_id: formValues.client_id, organization_id: formValues.org_id, file: formValues.batch_file, gateway: formValues.gateway })
      .then((data: any) => {
        if (data.message === 'There are no any crypto gateway assigned to client') toast.error(data.message);
        else { toast.success(data.message); handleClose(); fetchPayouts(); }
        setIsLoading(false);
      }).catch(() => setIsLoading(false));
  };

  return (
    <>
      {singleRoleData?.[0]?.crypto?.value?.add_crypto_payin_batch_file && (
        <button className="cpi-trigger-btn" onClick={() => { fetchOrganizations(); onOpen(); }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Crypto Batch Payin
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`cpi-panel${isOpen && !isCloseModal ? " cpi-panel--open" : " cpi-panel--close"}`}>
          <div className="cpi-glow" />

          <div className="cpi-header">
            <div className="cpi-header-brand">
              <div className="cpi-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <div>
                <h2 className="cpi-title">Crypto Batch Payin</h2>
                <p className="cpi-sub">Upload a batch CSV to create crypto payins</p>
              </div>
            </div>
            <button className="cpi-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
          </div>

          <div className="cpi-body">
            {/* Organization */}
            <div className="cpi-field-group">
              <label className="cpi-label">Organization <span className="cpi-req">*</span></label>
              <AntSelect showSearch allowClear placeholder="Select Organization"
                filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes(i.toLowerCase())}
                className="cpi-ant-select" style={{ width:"100%" }}
                options={organizations.map((m: any) => ({ ...m, label: m.name, value: m.id }))}
                value={formValues?.org_id || undefined}
                onChange={(e: any) => {
                  setFormValues((p: any) => ({ ...p, org_id: e, client_id:"", gateway:"" }));
                  setFormValuesErr((p: any) => ({ ...p, org_id:"" }));
                  setAllGateways([]); fetchClients(e);
                }}
                notFoundContent="Organization not found"
              />
              {formValuesErr?.org_id && <p className="cpi-err">{formValuesErr.org_id}</p>}
            </div>

            {/* Client */}
            <div className="cpi-field-group">
              <label className="cpi-label">Client <span className="cpi-req">*</span></label>
              <AntSelect showSearch allowClear placeholder="Select Client"
                filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes(i.toLowerCase())}
                className="cpi-ant-select" style={{ width:"100%" }}
                options={clients.map((m: any) => ({ label: m.name, value: m.id }))}
                value={formValues?.client_id || undefined}
                onChange={(e: any) => {
                  setFormValues((p: any) => ({ ...p, client_id: e, gateway:"" }));
                  setFormValuesErr((p: any) => ({ ...p, client_id:"" }));
                  setAllGateways([]); fetchGateways(e);
                }}
                notFoundContent="Client not found"
              />
              {formValuesErr?.client_id && <p className="cpi-err">{formValuesErr.client_id}</p>}
            </div>

            {/* Gateway */}
            <div className="cpi-field-group">
              <label className="cpi-label">Gateway <span className="cpi-req">*</span></label>
              <AntSelect showSearch allowClear placeholder="Select Gateway"
                filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes(i.toLowerCase())}
                className="cpi-ant-select" style={{ width:"100%" }}
                options={allGateways.map((m: any) => ({
                  ...m,
                  label: singleRoleData?.[0]?.crypto?.value?.show_crypto_payout_gateway_name ? m.name : m.id,
                  value: m.id,
                }))}
                value={formValues?.gateway || undefined}
                onChange={(e: any) => {
                  setFormValues((p: any) => ({ ...p, gateway: e }));
                  setFormValuesErr((p: any) => ({ ...p, gateway:"" }));
                }}
                notFoundContent="Gateway not found"
              />
              {formValuesErr?.gateway && <p className="cpi-err">{formValuesErr.gateway}</p>}
            </div>

            {/* CSV Upload */}
            <div className="cpi-field-group">
              <div className="cpi-csv-label-row">
                <label className="cpi-label">CSV File <span className="cpi-req">*</span></label>
                <a href={Batch} download="SampleBatch.csv" className="cpi-download-link">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download Sample CSV
                </a>
              </div>
              <FileDropzone label="Upload Batch CSV File" onFileSelect={setBatchFile} onRemoveFile={setRemoveFile} />
              {formValuesErr?.batch_file && <p className="cpi-err">{formValuesErr.batch_file}</p>}
            </div>

            {/* CSV headers info */}
            <div className="cpi-info-card">
              <div className="cpi-info-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div>
                <p className="cpi-info-title">Required CSV headers</p>
                <p className="cpi-info-text">invoice_currency, invoice_amount, payment_currency, payment_chain, customer_id, external_customer_no</p>
              </div>
            </div>
          </div>

          <div className="cpi-footer">
            <div className="cpi-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              All fields are required to proceed
            </div>
            <div className="cpi-footer-btns">
              <button className="cpi-btn-cancel" onClick={handleClose}>Cancel</button>
              <button className="cpi-btn-submit" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? <span className="cpi-btn-spinner" /> : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Add Batch</>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .cpi-trigger-btn { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; color:#fff; padding:7px 18px; border-radius:10px; border:none; background:linear-gradient(135deg,#6d28d9,#7c3aed); cursor:pointer; box-shadow:0 3px 12px rgba(124,58,237,.28); transition:all .15s; white-space:nowrap; }
        .cpi-trigger-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(124,58,237,.36); }

        .cpi-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:520px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
        .cpi-panel--open  { transform:translateX(0); }
        .cpi-panel--close { transform:translateX(100%); }

        .cpi-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#3b0764,#7c3aed,#a855f7,#7c3aed,#3b0764); background-size:200% 100%; animation:cpiGlow 3s linear infinite; }
        @keyframes cpiGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .cpi-header { padding:15px 26px; background:linear-gradient(135deg,#3b0764 0%,#6d28d9 60%,#7c3aed 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .cpi-header-brand { display:flex; align-items:center; gap:13px; }
        .cpi-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .cpi-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .cpi-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .cpi-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .cpi-close:hover { color:#fff; }

        .cpi-body { flex:1; padding:22px 26px; overflow-y:auto; display:flex; flex-direction:column; gap:16px; }
        .cpi-body::-webkit-scrollbar { width:4px; }
        .cpi-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

        .cpi-field-group { display:flex; flex-direction:column; gap:6px; }
        .cpi-label { font-size:13px; font-weight:700; color:#0f172a; }
        .cpi-req { color:#dc2626; }
        .cpi-err { font-size:12px; color:#dc2626; font-weight:500; }

        .cpi-ant-select {display:table !important;}
        .cpi-ant-select .ant-select-selector { height:44px !important; border:1.5px solid #e2e8f0 !important; border-radius:10px !important; background:#f8fafc !important; font-family:'Plus Jakarta Sans',sans-serif !important; }
        .cpi-ant-select .ant-select-selection-search-input { height:42px !important; }
        .cpi-ant-select .ant-select-selection-placeholder { line-height:42px !important; color:#cbd5e1 !important; font-size:13px !important; }
        .cpi-ant-select .ant-select-selection-item { line-height:42px !important; font-size:13px !important; font-weight:600 !important; }

        .cpi-csv-label-row { display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .cpi-download-link { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:600; color:#7c3aed; text-decoration:none; transition:color .15s; }
        .cpi-download-link:hover { color:#6d28d9; }

        .cpi-dropzone { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:22px; border:2px dashed #ddd6fe; border-radius:12px; cursor:pointer; min-height:90px; transition:all .2s; background:#faf5ff; }
        .cpi-dropzone:hover { border-color:#c4b5fd; background:#ede9fe; }
        .cpi-dropzone-file { display:flex; align-items:center; gap:8px; }
        .cpi-dropzone-filename { font-size:13px; font-weight:600; color:#0f172a; max-width:240px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .cpi-dropzone-remove { display:flex; align-items:center; justify-content:center; width:22px; height:22px; border-radius:50%; background:#fee2e2; border:none; color:#dc2626; cursor:pointer; flex-shrink:0; }
        .cpi-dropzone-placeholder { display:flex; flex-direction:column; align-items:center; }
        .cpi-dropzone-label { font-size:13px; color:#6d28d9; font-weight:600; }

        .cpi-info-card { display:flex; align-items:flex-start; gap:10px; background:#faf5ff; border:1.5px solid #ddd6fe; border-radius:11px; padding:13px 15px; }
        .cpi-info-icon { width:28px; height:28px; border-radius:8px; background:#ede9fe; border:1.5px solid #ddd6fe; display:flex; align-items:center; justify-content:center; color:#7c3aed; flex-shrink:0; }
        .cpi-info-title { font-size:12px; font-weight:700; color:#3b0764; margin-bottom:4px; }
        .cpi-info-text { font-size:11.5px; color:#6d28d9; font-weight:500; line-height:1.55; word-break:break-word; }

        .cpi-footer { padding:14px 26px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; flex-shrink:0; }
        .cpi-footer-note { display:flex; align-items:center; gap:5px; font-size:12px; color:#94a3b8; font-weight:500; }
        .cpi-footer-btns { display:flex; align-items:center; gap:8px; }
        .cpi-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .cpi-btn-cancel:hover { background:#f1f5f9; }
        .cpi-btn-submit { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#6d28d9,#7c3aed); cursor:pointer; box-shadow:0 4px 14px rgba(124,58,237,.3); transition:transform .15s, box-shadow .18s; }
        .cpi-btn-submit:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 24px rgba(124,58,237,.38); }
        .cpi-btn-submit:disabled { opacity:.5; cursor:not-allowed; transform:none; box-shadow:none; }
        .cpi-btn-spinner { display:inline-block; width:15px; height:15px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:cpiSpin .65s linear infinite; }
        @keyframes cpiSpin { to{transform:rotate(360deg)} }

        @media (max-width:540px) { .cpi-panel { max-width:100%; } .cpi-header, .cpi-body, .cpi-footer { padding-left:18px; padding-right:18px; } }
      `}</style>
    </>
  );
};

export default AddBatchFile;